import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Mail, MapPin, Phone, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all fields");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent! We'll get back to you soon.");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    "bg-black/50 border-[#D4AF37]/30 focus:border-[#D4AF37] text-white placeholder:text-white/30 font-montserrat h-12";
  const labelClass =
    "text-[#D4AF37]/80 text-xs tracking-[0.2em] uppercase font-montserrat mb-2";

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-32 relative"
    >
      <div className="absolute inset-0 section-glow-pink" />
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <p className="font-great-vibes text-[#FF007F] text-2xl md:text-3xl mb-4">
            Get In Touch
          </p>
          <h2 className="font-playfair text-[#D4AF37] text-4xl md:text-5xl font-bold">
            Contact Us
          </h2>
          <div className="gold-line w-24 mx-auto mt-6" />
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Contact Form */}
          <motion.form
            onSubmit={handleSubmit}
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="space-y-6"
            data-testid="contact-form"
          >
            <div>
              <Label className={labelClass}>Your Name</Label>
              <Input
                data-testid="contact-name-input"
                className={inputClass}
                placeholder="Enter your name"
                value={form.name}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, name: e.target.value }))
                }
              />
            </div>
            <div>
              <Label className={labelClass}>Email Address</Label>
              <Input
                data-testid="contact-email-input"
                type="email"
                className={inputClass}
                placeholder="your@email.com"
                value={form.email}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, email: e.target.value }))
                }
              />
            </div>
            <div>
              <Label className={labelClass}>Message</Label>
              <Textarea
                data-testid="contact-message-input"
                className="bg-black/50 border-[#D4AF37]/30 focus:border-[#D4AF37] text-white placeholder:text-white/30 font-montserrat min-h-[140px]"
                placeholder="How can we help you?"
                value={form.message}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, message: e.target.value }))
                }
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit-btn"
              className="btn-gold px-10 py-4 rounded-full text-sm font-montserrat flex items-center gap-3 disabled:opacity-50"
            >
              <Send className="w-4 h-4" />
              {submitting ? "Sending..." : "Send Message"}
            </button>
          </motion.form>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-8"
          >
            <div>
              <h3 className="font-playfair text-white text-xl mb-8">
                Reach Out to Us
              </h3>

              <div className="space-y-6">
                <a
                  href="mailto:info@vickiannmysteryscoop.com"
                  data-testid="contact-email-link"
                  className="flex items-center gap-4 group"
                >
                  <div className="w-12 h-12 rounded-full border border-[#D4AF37]/30 flex items-center justify-center group-hover:border-[#D4AF37] transition-colors duration-300">
                    <Mail
                      className="w-5 h-5 text-[#D4AF37]"
                      strokeWidth={1.5}
                    />
                  </div>
                  <div>
                    <p className="font-montserrat text-white/40 text-xs tracking-widest uppercase">
                      Email
                    </p>
                    <p className="font-montserrat text-white/80 text-sm mt-1">
                      info@vickiannmysteryscoop.com
                    </p>
                  </div>
                </a>

                <a
                  href="tel:+14709804669"
                  data-testid="contact-phone-link"
                  className="flex items-center gap-4 group"
                >
                  <div className="w-12 h-12 rounded-full border border-[#D4AF37]/30 flex items-center justify-center group-hover:border-[#D4AF37] transition-colors duration-300">
                    <Phone
                      className="w-5 h-5 text-[#D4AF37]"
                      strokeWidth={1.5}
                    />
                  </div>
                  <div>
                    <p className="font-montserrat text-white/40 text-xs tracking-widest uppercase">
                      Phone
                    </p>
                    <p className="font-montserrat text-white/80 text-sm mt-1">
                      +1 (470) 980-4669
                    </p>
                  </div>
                </a>

                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full border border-[#D4AF37]/30 flex items-center justify-center">
                    <MapPin
                      className="w-5 h-5 text-[#D4AF37]"
                      strokeWidth={1.5}
                    />
                  </div>
                  <div>
                    <p className="font-montserrat text-white/40 text-xs tracking-widest uppercase">
                      Location
                    </p>
                    <p className="font-montserrat text-white/80 text-sm mt-1">
                      GA 30303, USA
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Website URL */}
          
          </motion.div>
        </div>
      </div>
    </section>
  );
}
